import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { features } from '@/mocks/data';

const milestones = [
  { year: '2018', title: '品牌创立', desc: '蜜桃在线正式成立，以"让娱乐更简单"为初心开启征程。' },
  { year: '2019', title: '用户突破百万', desc: '凭借优质内容和流畅体验，注册用户数突破100万。' },
  { year: '2020', title: '技术全面升级', desc: '引入AI推荐系统和全球CDN加速，体验全面跃升。' },
  { year: '2021', title: '内容生态扩展', desc: '新增直播、游戏、音乐等频道，构建多元内容矩阵。' },
  { year: '2022', title: '用户突破千万', desc: '累计注册用户突破1000万，覆盖全国各省市。' },
  { year: '2023', title: '安全体系完善', desc: '建立七层内容审核与隐私保护体系，获行业安全认证。' },
  { year: '2024', title: '移动端全面优化', desc: '推出全新移动端体验，适配全平台无缝切换。' },
  { year: '2025', title: '社区生态繁荣', desc: '社区日活跃用户突破200万，形成温暖友好的社区文化。' },
  { year: '2026', title: '全新品牌升级', desc: '品牌全面焕新，以更优质的体验服务每一位用户。' },
];

const stats = [
  { value: '1500万+', label: '累计注册用户' },
  { value: '300万+', label: '日活跃用户' },
  { value: '99.9%', label: '平台可用率' },
  { value: '200+', label: '全国加速节点' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '蜜桃在线',
    url: 'https://www.mitaozaixian.com',
    description: '蜜桃在线是您首选的在线娱乐平台，提供极速稳定的访问体验、丰富多样的内容资源与贴心的用户服务。',
    email: 'service@mitaozaixian.com',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'service@mitaozaixian.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于蜜桃在线',
    description: '了解蜜桃在线的品牌故事、发展历程与核心优势',
    url: 'https://www.mitaozaixian.com/about',
  };

  return (
    <Layout>
      <SEOHead
        title="关于我们 - 蜜桃在线 | 品牌介绍·信誉保障·联系我们"
        description="蜜桃在线成立于2018年，是国内领先的在线娱乐平台。了解蜜桃在线的品牌故事、发展历程、核心优势及联系方式。蜜桃在线始终以用户体验为核心。"
        keywords="蜜桃在线,关于蜜桃在线,蜜桃在线品牌,蜜桃在线官网,蜜桃在线联系方式,在线娱乐平台"
        canonicalUrl="https://www.mitaozaixian.com/about"
      />

      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>

      <div className="pt-20">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="relative w-full px-4 md:px-6 py-16 md:py-20 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://readdy.ai/api/search-image?query=Abstract%20warm%20peach%20gradient%20with%20soft%20golden%20light%20rays%20emanating%20from%20center%2C%20elegant%20minimal%20composition%2C%20warm%20and%20professional%20atmosphere%2C%20brand%20storytelling%20visual%2C%20clean%20modern%20aesthetic&width=1920&height=500&seq=about-hero-01&orientation=landscape')`,
            }}
          >
          </div>
          <div className="absolute inset-0 bg-black/25"></div>
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <h1 className="text-2xl md:text-4xl font-heading text-background-50 mb-4">
              关于<strong>蜜桃在线</strong>
            </h1>
            <p className="text-sm md:text-base text-background-50/75 max-w-xl mx-auto leading-relaxed">
              以用户体验为核心，持续打造更优质的在线娱乐服务平台。从2018年创立至今，我们始终坚守初心，致力于为每一位用户带来极致的娱乐体验。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                品牌故事
              </span>
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                我们的<strong>成长历程</strong>
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {milestones.map((m, i) => (
                <div key={i} className="p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300">
                  <span className="text-2xl font-heading text-primary-500">{m.year}</span>
                  <h3 className="font-heading text-base text-foreground-900 mt-1 mb-1.5">{m.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, i) => (
                <div key={i} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="text-3xl md:text-4xl font-heading text-primary-500 mb-2">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core Advantages */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                核心优势
              </span>
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                值得信赖的<strong>品质保障</strong>
              </h2>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto">
                蜜桃在线始终将用户体验和内容品质放在首位，以下是我们获得广泛认可的核心能力
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {features.map((f, i) => (
                <div key={i} className="flex gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                    <i className={`${f.icon} text-xl text-primary-600`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-base text-foreground-900 mb-1.5">{f.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{f.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                联系我们
              </span>
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                期待您的<strong>宝贵意见</strong>
              </h2>
              <p className="text-sm text-foreground-600">
                如有任何问题或建议，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              <div className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-lg text-primary-600"></i>
                </div>
                <h3 className="font-heading text-sm text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-500">service@mitaozaixian.com</p>
              </div>
              <div className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-customer-service-2-line text-lg text-accent-600"></i>
                </div>
                <h3 className="font-heading text-sm text-foreground-900 mb-1">在线客服</h3>
                <p className="text-xs text-foreground-500">7×24小时在线服务</p>
              </div>
              <div className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-line text-lg text-secondary-600"></i>
                </div>
                <h3 className="font-heading text-sm text-foreground-900 mb-1">公司地址</h3>
                <p className="text-xs text-foreground-500">中国·北京</p>
              </div>
            </div>

            <div className="rounded-xl bg-background-50 border border-background-200/70 p-6 md:p-8">
              <form
                id="contact-form"
                data-readdy-form
                action="https://readdy.ai/api/form/d9sls9tlbrt1qcgaj5s0"
                method="POST"
                onSubmit={handleSubmit}
              >
                <div className="spam-guard">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入主题"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                </div>

                {formStatus === 'success' && (
                  <div className="mb-4 p-4 rounded-lg bg-primary-50 border border-primary-200 text-sm text-primary-700">
                    <div className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill text-primary-500"></i>
                      感谢您的留言！我们会在第一时间回复您。
                    </div>
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                    <div className="flex items-center gap-2">
                      <i className="ri-error-warning-fill text-red-500"></i>
                      {formError || '提交失败，请稍后重试'}
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  提交留言
                </button>
              </form>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}